import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  adminRefresh,
  getAdminExpiresAt,
  getAdminToken,
  setAdminToken,
} from "@/lib/api";

// Show the renewal toast this many ms before the session would expire.
const WARN_BEFORE_MS = 10 * 60 * 1000; // 10 minutes
// Re-check expiry on this interval (covers tab being backgrounded then resumed).
const POLL_INTERVAL_MS = 60 * 1000; // 1 minute

/**
 * Watches admin session expiry. When the session is within WARN_BEFORE_MS of
 * expiring, surfaces a persistent toast with a "Keep me signed in" action that
 * rotates the token via /admin/refresh. On user inactivity past expiry,
 * onExpired() is called so the host page can sign the user out gracefully.
 */
export function useSessionRenewal({ onExpired } = {}) {
  const warnedRef = useRef(false);
  const refreshingRef = useRef(false);
  const toastIdRef = useRef(null);

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      const { token, expires_at } = await adminRefresh();
      setAdminToken(token, expires_at);
      warnedRef.current = false;
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
      toast.success("Session extended.");
    } catch (err) {
      console.warn("Session refresh failed:", err);
      toast.error("Couldn't extend session. Please sign in again.");
      if (onExpired) onExpired();
    } finally {
      refreshingRef.current = false;
    }
  }, [onExpired]);

  useEffect(() => {
    if (!getAdminToken()) return undefined;

    const check = () => {
      const token = getAdminToken();
      const expiresAtIso = getAdminExpiresAt();
      if (!token || !expiresAtIso) return;
      const expiresAt = new Date(expiresAtIso).getTime();
      if (Number.isNaN(expiresAt)) return;
      const remaining = expiresAt - Date.now();

      if (remaining <= 0) {
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
          toastIdRef.current = null;
        }
        toast.error("Your admin session expired.");
        if (onExpired) onExpired();
        return;
      }

      if (remaining <= WARN_BEFORE_MS && !warnedRef.current) {
        warnedRef.current = true;
        const minutesLeft = Math.max(1, Math.round(remaining / 60000));
        toastIdRef.current = toast.warning(
          `Your admin session expires in ~${minutesLeft} min. Unsaved edits will be lost.`,
          {
            duration: Infinity,
            action: {
              label: "Keep me signed in",
              onClick: refresh,
            },
          }
        );
      }
    };

    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    };
  }, [refresh, onExpired]);

  return { refreshNow: refresh };
}
